import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { getSavedMicrosoftProviderNames } from '../DataProvider/DataProvider.utils';
import {
  DataProviderField,
  DEFAULT_CATEGORY_STRATEGIES,
  matchesSuffix,
  SubProvider
} from '../SourceCreate.constants';
import {
  microsoftDataProvidersCategoriesAtom,
  selectedSourceDetailsAtom
} from '../SourceCreate.store';
import {
  type BasicSourceLogTypeDTO,
  SourceDialogMode
} from '../SourceCreate.types';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Filters categories to show only those available for the specified provider.
 *
 * Business Rules:
 * - Graph API: Only categories ending with the specified suffix (e.g., ".graph")
 * - O365 API: Only categories ending with the specified suffix (e.g., ".o365")
 * - Other providers: All categories for the source type
 *
 * @param categories - All available Microsoft data provider categories
 * @param sourceTypeId - The cloud source type ID
 * @param subProvider - The specific Microsoft provider (GraphAPI, O365API, etc.)
 * @param suffix - Optional category name suffix for filtering
 */
const filterAvailableCategories = (
  categories: BasicSourceLogTypeDTO[],
  sourceTypeId: number,
  subProvider?: string,
  suffix?: string
): BasicSourceLogTypeDTO[] => {
  return categories.filter((cat) => {
    // Must match the source type
    if (cat.source_type_id !== sourceTypeId) return false;

    // Apply provider-specific filtering rules
    switch (subProvider) {
      case SubProvider.GraphAPI:
      case SubProvider.O365API:
        return matchesSuffix(cat.name, suffix);
      default:
        return true;
    }
  });
};

/**
 * Selects categories that should be pre-checked when creating/editing a source.
 *
 * Business Rules by Provider:
 * - O365 (legacy): All categories EXCEPT Azure AD Audit (requires Graph API)
 * - Defender: All categories EXCEPT AAD Sign-in (licensing restriction)
 * - Graph API: Categories matching suffix EXCEPT sign-in (requires enterprise license)
 * - O365 API: All categories matching suffix
 *
 * Edit Mode Special Case:
 * If the provider already exists on the source, return no defaults to preserve
 * the user's existing selection.
 *
 * @param categories - All available Microsoft data provider categories
 * @param sourceTypeId - The cloud source type ID
 * @param subProvider - The specific Microsoft provider
 * @param suffix - Optional category name suffix for filtering
 * @param mode - Form mode ('New' or 'Edit')
 * @param existingProviders - Providers already configured on this source (Edit mode)
 */
const selectDefaultCategories = (
  categories: BasicSourceLogTypeDTO[],
  sourceTypeId: number,
  subProvider?: string,
  suffix?: string,
  mode?: string,
  existingProviders: string[] = []
): BasicSourceLogTypeDTO[] => {
  return categories.filter((cat) => {
    // Must match the source type
    if (cat.source_type_id !== sourceTypeId) return false;

    const strategy = DEFAULT_CATEGORY_STRATEGIES[subProvider as SubProvider];
    if (!strategy) return true;

    // If provider already exists, don't override user's current selection
    if (
      mode === SourceDialogMode.Edit &&
      existingProviders.includes(subProvider || '')
    ) {
      return false;
    }

    return strategy(cat, suffix);
  });
};

// ============================================================================
// Utility Exports
// ============================================================================

/**
 * Builds form field paths for data provider nested fields.
 * Example: buildDataProviderFieldPath("O365", "subscription_type") → "O365.subscription_type"
 */
export const buildDataProviderFieldPath = (
  provider: string,
  fieldName: DataProviderField = DataProviderField.SubscriptionType
) => `${provider}.${fieldName}`;

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Filters and pre-selects categories based on source type and Microsoft provider.
 *
 * This hook manages the complex business rules around which log categories are
 * available and pre-selected for different Microsoft data providers (O365, Graph API,
 * Defender, etc.). The rules account for licensing restrictions, API capabilities,
 * and whether the source is being created or edited.
 *
 * @param sourceTypeId - The cloud source type ID (e.g., Microsoft 365, Entra ID)
 * @param suffix - Optional category name suffix for filtering (e.g., ".graph", ".o365")
 * @param subProvider - The specific Microsoft provider (O365, GraphAPI, O365API, Defender)
 * @param mode - Form mode ('New' for creation, 'Edit' for modification)
 *
 * @returns Object containing:
 *  - categories: Filtered list of available category objects
 *  - categoryNames: Array of category name strings
 *  - defaultSelectedCategories: Array of category names that should be pre-checked
 *
 * @example
 * const { categories, defaultSelectedCategories } = useCategoriesBySourceType(
 *   sourceTypeId,
 *   ".graph",
 *   SubProvider.GraphAPI,
 *   "New"
 * );
 */
export const useCategoriesBySourceType = (
  sourceTypeId: number,
  suffix?: string,
  subProvider?: string,
  mode?: string
) => {
  const categories = useAtomValue(microsoftDataProvidersCategoriesAtom);
  const selectedSourceDetails = useAtomValue(selectedSourceDetailsAtom);

  // In Edit mode, determine which providers already exist to avoid conflicts
  const existingProviders = useMemo(
    () => getSavedMicrosoftProviderNames(selectedSourceDetails),
    [selectedSourceDetails]
  );

  // Filter categories available for this source type and provider
  const availableCategories = useMemo(
    () =>
      filterAvailableCategories(categories, sourceTypeId, subProvider, suffix),
    [categories, sourceTypeId, subProvider, suffix]
  );

  // Determine which categories should be pre-selected based on business rules
  const preSelectedCategories = useMemo(
    () =>
      selectDefaultCategories(
        categories,
        sourceTypeId,
        subProvider,
        suffix,
        mode,
        existingProviders
      ),
    [categories, sourceTypeId, subProvider, suffix, mode, existingProviders]
  );

  return {
    categories: availableCategories,
    categoryNames: availableCategories.map((c) => c.name),
    defaultSelectedCategories: preSelectedCategories.map((c) => c.name)
  };
};
